import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextInput from '@/components/common/TextInput';
import { useTranslation } from '@/hooks/useTranslation';
import { NAMESPACES } from '@/i18n/namespaces';
import { useForgotPassword, useCheckPasscode, useChangePassword } from '@/features/auth/hooks/useAuth';
import { toast } from 'react-toastify';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'email' | 'code' | 'password'>('email');
  const [direction, setDirection] = useState<'forward' | 'backward' | 'none'>('none');
  const [resetToken, setResetToken] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { t } = useTranslation(NAMESPACES.AUTH);
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  
  // Custom hooks for API interactions
  const { requestPasswordReset, loading: requestingReset } = useForgotPassword();
  const { verifyCode, loading: verifyingCode } = useCheckPasscode();
  const { resetPassword, loading: resettingPassword } = useChangePassword();
  
  const isLoading = requestingReset || verifyingCode || resettingPassword;

  // Get animation class
  const getAnimationClass = (currentStep: 'email' | 'code' | 'password') => {
    if (direction === 'none') {
      return 'animate-fade-in';
    }
    
    if (step === currentStep) {
      return 'animate-fade-in';
    } else {
      return 'animate-fade-out';
    }
  };
  
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setCode(value);
    }
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };
  
  const handleRequestCode = async () => {
    if (!email.trim() || !email.includes('@')) {
      toast.error(tErrors('validation.invalidEmail'));
      return;
    }
    
    const success = await requestPasswordReset(email);
    
    if (success) {
      setDirection('forward');
      // Add a small delay to allow animation to complete
      setTimeout(() => {
        setStep('code');
      }, 200);
    }
  };
  
  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      toast.error(tErrors('validation.invalidCode'));
      return;
    }
    
    const token = await verifyCode(email, code);
    
    if (token) {
      setResetToken(token);
      setDirection('forward');
      // Add a small delay to allow animation to complete
      setTimeout(() => {
        setStep('password');
      }, 200);
    }
  };
  
  const handleResetPassword = async () => {
    if (!password.trim() || password.length < 6) {
      toast.error(tErrors('validation.weakPassword'));
      return;
    }
    
    if (!resetToken) {
      toast.error(tErrors('validation.sessionExpired'));
      // Reset the flow if token is missing
      setDirection('backward');
      setTimeout(() => {
        setStep('email');
      }, 200);
      return;
    }
    
    await resetPassword(resetToken, password);
  };
  
  const handleBack = () => {
    if (step === 'code') {
      setDirection('backward');
      setTimeout(() => {
        setStep('email');
      }, 200);
    } else if (step === 'password') {
      setDirection('backward');
      setTimeout(() => {
        setStep('code');
      }, 200);
    } else {
      navigate('/login');
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (step === 'email' && !isLoading) {
        handleRequestCode();
      } else if (step === 'code' && !isLoading) {
        handleVerifyCode();
      } else if (step === 'password' && !isLoading) {
        handleResetPassword();
      }
    }
  };
  
  return (
    <div className="h-dvh flex flex-col bg-black text-white">
      <div className="flex-1 flex flex-col px-6">
        {/* Email Step */}
        {step === 'email' && (
          <div className={`flex-1 flex flex-col ${getAnimationClass('email')}`}>
            {/* Top section with headings */}
            <div className="pt-[32px] mb-16 flex justify-center">
              <div className="max-w-[260px]">
                <h1 className="text-white text-center text-[27px] font-medium leading-6 mb-[12px]" 
                  style={{
                    letterSpacing: '-0.337px'
                  }}>
                  {t('forgotPassword.title')}
                </h1>
                <h2 className="text-center text-[18px] font-normal leading-5" 
                  style={{
                    color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                    letterSpacing: '-0.225px'
                  }}>
                  {t('forgotPassword.enterEmail')}
                </h2>
              </div>
            </div>
            
            {/* Middle section with input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <TextInput
                  type="email"
                  name="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={handleEmailChange}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            
            {/* Bottom section with buttons */}
            <div className="flex justify-center items-center gap-4 mb-[32px]">
              <button 
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('forgotPassword.backToLogin')}
              </button>
              
              <button 
                className="btn btn-primary whitespace-nowrap"
                onClick={handleRequestCode}
                disabled={!email.trim() || !email.includes('@') || isLoading}
              >
                {requestingReset ? t('forgotPassword.requestingCode') : t('forgotPassword.next')}
              </button>
            </div>
          </div>
        )}
        
        {/* Code Verification Step */}
        {step === 'code' && (
          <div className={`flex-1 flex flex-col ${getAnimationClass('code')}`}>
            {/* Top section with headings */}
            <div className="pt-[32px] mb-16 flex justify-center">
              <div className="max-w-[260px]">
                <h1 className="text-white text-center text-[27px] font-medium leading-6 mb-[12px]" 
                  style={{
                    letterSpacing: '-0.337px'
                  }}>
                  {t('forgotPassword.checkInbox')}
                </h1>
                <h2 className="text-center text-[18px] font-normal leading-5" 
                  style={{
                    color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                    letterSpacing: '-0.225px'
                  }}>
                  {t('forgotPassword.codePrompt', { email })}
                </h2>
              </div>
            </div>
            
            {/* Middle section with input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <TextInput
                  type="text"
                  name="code"
                  placeholder={t('forgotPassword.codePlaceholder')}
                  value={code}
                  onChange={handleCodeChange}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  autoFocus
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            </div>
            
            {/* Bottom section with buttons */}
            <div className="flex justify-center items-center gap-4 mb-[32px]">
              <button 
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('forgotPassword.back')}
              </button>
              
              <button 
                className="btn btn-primary whitespace-nowrap"
                onClick={handleVerifyCode}
                disabled={code.length !== 6 || isLoading}
              >
                {verifyingCode ? t('forgotPassword.verifyingCode') : t('forgotPassword.next')}
              </button>
            </div>
          </div>
        )}
        
        {/* New Password Step */}
        {step === 'password' && (
          <div className={`flex-1 flex flex-col ${getAnimationClass('password')}`}>
            {/* Top section with headings */}
            <div className="pt-[32px] mb-16 flex justify-center">
              <div className="max-w-[260px]">
                <h1 className="text-white text-center text-[27px] font-medium leading-6 mb-[12px]" 
                  style={{
                    letterSpacing: '-0.337px'
                  }}>
                  {t('forgotPassword.createNewPassword')}
                </h1>
                <h2 className="text-center text-[18px] font-normal leading-5" 
                  style={{
                    color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                    letterSpacing: '-0.225px'
                  }}>
                  {t('forgotPassword.newPasswordPrompt')}
                </h2>
              </div>
            </div>
            
            {/* Middle section with input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <TextInput
                  type="password"
                  name="password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            
            {/* Bottom section with buttons */}
            <div className="flex justify-center items-center gap-4 mb-[32px]">
              <button 
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('forgotPassword.back')}
              </button>
              
              <button 
                className="btn btn-primary whitespace-nowrap"
                onClick={handleResetPassword}
                disabled={!password.trim() || password.length < 6 || isLoading}
              >
                {resettingPassword ? t('forgotPassword.resetting') : t('forgotPassword.resetPassword')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword; 